import { formatCurrencyBRL } from "../../utils/utils";

/*
  Vocabulário visual do CRM.

  Os ESTÁGIOS e as FILAS não são definidos aqui: vêm do servidor
  (admin/services/crm/lifecycle.js e workQueueService.js), com rótulo,
  descrição e `tone`. Este arquivo só traduz `tone` em classes de Tailwind.

  O motivo é direto: estágio é regra de negócio. Se a lista estivesse
  duplicada no front, um estágio novo apareceria com o id cru na tela
  ("FREE_DORMANT") até alguém lembrar de mexer aqui também — e, pior, a
  descrição mostrada poderia contradizer a regra que o servidor aplica.
*/

export const TONE_CLASSES = {
  success: {
    badge: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    dot: "bg-emerald-500",
    bar: "bg-emerald-500",
    soft: "bg-emerald-50",
  },
  info: {
    badge: "bg-sky-50 text-sky-700 ring-sky-200",
    dot: "bg-sky-500",
    bar: "bg-sky-500",
    soft: "bg-sky-50",
  },
  warning: {
    badge: "bg-amber-50 text-amber-700 ring-amber-200",
    dot: "bg-amber-500",
    bar: "bg-amber-500",
    soft: "bg-amber-50",
  },
  danger: {
    badge: "bg-rose-50 text-rose-700 ring-rose-200",
    dot: "bg-rose-500",
    bar: "bg-rose-500",
    soft: "bg-rose-50",
  },
  neutral: {
    badge: "bg-slate-100 text-slate-600 ring-slate-200",
    dot: "bg-slate-400",
    bar: "bg-slate-400",
    soft: "bg-slate-50",
  },
};

export const toneOf = (tone) => TONE_CLASSES[tone] || TONE_CLASSES.neutral;

export const INTERACTION_TYPES = [
  { value: "CALL", label: "Ligação" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "EMAIL", label: "E-mail" },
  { value: "MEETING", label: "Reunião" },
  { value: "NOTE", label: "Anotação" },
];

export const INTERACTION_LABELS = {
  CALL: "Ligação",
  WHATSAPP: "WhatsApp",
  EMAIL: "E-mail",
  MEETING: "Reunião",
  NOTE: "Anotação",
  SYSTEM: "Sistema",
};

export const PRIORITY_OPTIONS = [
  { value: "LOW", label: "Baixa" },
  { value: "NORMAL", label: "Normal" },
  { value: "HIGH", label: "Alta" },
];

export const PRIORITY_LABELS = { LOW: "Baixa", NORMAL: "Normal", HIGH: "Alta" };

export const formatMoney = (value) => formatCurrencyBRL.format(Number(value) || 0);

export const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR").format(Number(value) || 0);

export const formatPercent = (value) =>
  `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(
    Number(value) || 0
  )}%`;

const DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export const formatDate = (value) => {
  if (!value) return "—";
  const parsed = parseApiDate(value);
  return parsed ? DATE_FORMATTER.format(parsed) : "—";
};

/*
  `dueDate` vem do MySQL como "2026-10-06" (DATEONLY). `new Date("2026-10-06")`
  é meia-noite UTC, e exibir isso em -03:00 mostra 05/10 — a tarefa aparecia
  vencendo um dia antes do que foi escolhido. Para string date-only os
  componentes são montados à mão, sem passar pelo parser de UTC.
*/
export const parseApiDate = (value) => {
  if (!value) return null;

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** "há 3 dias", "hoje", "em 2 dias" — o tempo relativo é o que se lê rápido. */
export const formatRelativeDays = (value) => {
  const parsed = parseApiDate(value);
  if (!parsed) return "—";

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTarget = new Date(parsed);
  startOfTarget.setHours(0, 0, 0, 0);

  const days = Math.round(
    (startOfTarget.getTime() - startOfToday.getTime()) / 86_400_000
  );

  if (days === 0) return "hoje";
  if (days === 1) return "amanhã";
  if (days === -1) return "ontem";
  if (days < 0) return `há ${Math.abs(days)} dias`;
  return `em ${days} dias`;
};

export const formatDaysCount = (days) => {
  if (days === null || days === undefined) return "—";
  const value = Number(days);
  if (value === 0) return "hoje";
  if (value === 1) return "1 dia";
  return `${formatNumber(value)} dias`;
};

/** Link de WhatsApp a partir do telefone cadastrado. */
export const whatsappLink = (phoneNumber) => {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  if (digits.length < 10) return null;

  // Número sem DDI recebe o do Brasil: é o cadastro padrão do app.
  const withCountry = digits.length <= 11 ? `55${digits}` : digits;
  return `https://wa.me/${withCountry}`;
};

export const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
};

export const PLAN_TONE = {
  DIAMOND: "info",
  PRO: "success",
  ESSENTIAL: "warning",
  FREE: "neutral",
};
